import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Truck, Plus, Printer, CheckCircle, Trash, ArrowSquareOut, Package, Boat, Eye, CaretDown, UploadSimple, MapPin, CalendarBlank } from "@phosphor-icons/react";
import PrintLabelsDialog from "@/components/PrintLabelsDialog";
import ShipmentImportDialog from "@/components/ShipmentImportDialog";
import ReceiveRollDialog from "@/components/ReceiveRollDialog";
import TrackingDialog from "@/components/TrackingDialog";
import { SUPPLY_TYPES, supplyTagClass } from "@/lib/incoming";
import { trackingUrl, carrierLabel } from "@/lib/carriers";

function TrackingLink({ po, className = "" }) {
  const num = (po?.tracking_number || "").trim();
  if (!num) return null;
  const url = trackingUrl(po.carrier, num);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 ${className}`}
      data-testid={`tracking-link-${po.id}`}
      title={`Track with ${carrierLabel(po.carrier) || "courier"}`}
    >
      <Truck size={11} weight="fill" /> {carrierLabel(po.carrier) ? `${carrierLabel(po.carrier)} · ` : ""}{num}
    </a>
  );
}

const empty = {
  product_id: "",
  batch_number: "",
  manufacturing_date: new Date().toISOString().slice(0, 10),
  expected_length_m: 22.86,
  width_m: 1.52,
  location: "In Transit",
  supply_type: "",
  expected_arrival_date: "",
  notes: "",
};

function StatusBadge({ status }) {
  const cls = status === "received"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${cls}`}>{status}</span>;
}

function TypeTag({ type }) {
  if (type === "po") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border bg-blue-50 text-blue-700 border-blue-200" data-testid="tag-shipment">
        <Boat size={12} weight="fill" /> Shipment
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border bg-slate-100 text-slate-600 border-slate-200" data-testid="tag-roll">
      <Package size={12} weight="fill" /> Roll
    </span>
  );
}

function SupplyBadge({ type }) {
  if (!type) return null;
  return (
    <span className={`inline-block text-[9px] font-mono tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-sm border ${supplyTagClass(type)}`} data-testid="supply-tag">
      {type}
    </span>
  );
}

function fmtDay(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return d; }
}

function fmtOrdered(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function LocationChip({ location }) {
  if ((location || "") === "In Transit") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300 font-semibold" data-testid="location-in-transit">
        <MapPin size={11} weight="fill" /> In Transit
      </span>
    );
  }
  return <span className="text-[10px] text-slate-500">{location || "—"}</span>;
}

export default function IncomingStockPage() {
  const [pos, setPos] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [products, setProducts] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [series, setSeries] = useState([]);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [printOpen, setPrintOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveTargets, setReceiveTargets] = useState([]);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingPo, setTrackingPo] = useState(null);

  // Per-roll receive form: expand the shipment's roll-type line items into individual roll slots.
  const rollSlots = useMemo(() => {
    if (!detail?.items) return [];
    const slots = [];
    detail.items.forEach((it, ii) => {
      if (it.inventory_type === "count") return;
      const length = it.unit === "meters" ? Number(it.quantity) : Number(it.length_per_roll_m) || 0;
      const n = it.unit === "meters" ? 1 : Math.max(0, Math.floor(Number(it.quantity) || 0));
      for (let k = 0; k < n; k++) {
        if (length <= 0) continue;
        slots.push({
          key: `${ii}-${k}`,
          product_name: it.product_name,
          base_sku: it.base_sku,
          color_hex: it.color_hex,
          series_name: it.series_name,
          length,
          is_order: it.purchase_type === "order",
          order_ref: it.order_reference,
          default_location: it.default_location || "",
        });
      }
    });
    return slots;
  }, [detail]);
  const countItems = useMemo(() => (detail?.items || []).filter((it) => it.inventory_type === "count"), [detail]);
  const [rollForms, setRollForms] = useState([]);
  useEffect(() => {
    setRollForms(rollSlots.map((s) => ({ batch_number: "", manufacturing_date: "", location: s.default_location || "" })));
  }, [rollSlots]);
  const rollsComplete = rollForms.length === rollSlots.length && rollForms.every((f) => f.batch_number.trim() && f.manufacturing_date.trim());
  const setRoll = (i, patch) => setRollForms((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const fillDown = (i) => setRollForms((prev) => prev.map((f, idx) => (idx > i ? { ...prev[i] } : f)));

  const load = async () => {
    try {
      const [{ data: po }, { data: r }, { data: p }, { data: m }, { data: s }] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/rolls/incoming-list"),
        api.get("/products"),
        api.get("/manufacturers"),
        api.get("/series"),
      ]);
      setPos(po);
      setRolls(r);
      setProducts(p);
      setMfrs(m);
      setSeries(s);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load incoming stock");
    }
  };
  useEffect(() => { load(); }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const mfrMap = Object.fromEntries(mfrs.map((m) => [m.id, m]));
  const seriesMap = Object.fromEntries(series.map((s) => [s.id, s]));
  const rollProducts = products.filter((p) => p.inventory_type !== "count");

  // Unified, tagged, chronological list of PENDING shipments + individual rolls
  const rows = useMemo(() => {
    const poRows = pos.filter((po) => po.status !== "received").map((po) => ({ _type: "po", _created: po.created_at, ...po }));
    const rollRows = rolls.map((r) => ({ _type: "roll", _created: r.created_at, ...r }));
    return [...poRows, ...rollRows].sort((a, b) => new Date(b._created) - new Date(a._created));
  }, [pos, rolls]);

  // Received shipments, grouped into dated blocks (newest day first)
  const receivedBlocks = useMemo(() => {
    const received = pos.filter((po) => po.status === "received");
    const groups = {};
    for (const po of received) {
      const d = (po.received_at || po.created_at || "").slice(0, 10) || "—";
      (groups[d] = groups[d] || []).push(po);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [pos]);

  const openCreate = () => {
    setForm({ ...empty, product_id: rollProducts[0]?.id || "" });
    setRegisterOpen(true);
  };

  const onSave = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        expected_length_m: Number(form.expected_length_m),
        width_m: Number(form.width_m),
        expected_arrival_date: form.expected_arrival_date || null,
      };
      await api.post("/rolls/incoming", payload);
      toast.success("Incoming roll registered — label can be printed");
      setRegisterOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const onReceive = (id) => {
    setReceiveTargets([id]);
    setReceiveOpen(true);
  };

  const onReceiveSelected = () => {
    if (selected.size === 0) return;
    setReceiveTargets([...selected]);
    setReceiveOpen(true);
  };

  const confirmReceive = async (mode, orderRef) => {
    setReceiveSubmitting(true);
    try {
      const { data } = await api.post("/rolls/receive-bulk", {
        ids: receiveTargets,
        receive_as: mode,
        order_reference: orderRef,
      });
      const n = data.received || 0;
      toast.success(
        mode === "order"
          ? `Allocated ${n} roll${n === 1 ? "" : "s"} to order "${orderRef}" (marked depleted)`
          : `Received ${n} roll${n === 1 ? "" : "s"} → moved to Active stock`
      );
      setReceiveOpen(false);
      setSelected((s) => {
        const n2 = new Set(s);
        receiveTargets.forEach((id) => n2.delete(id));
        return n2;
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setReceiveSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/rolls/${id}`);
      toast.success("Incoming roll cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openDetail = async (pid) => {
    try {
      const { data } = await api.get(`/purchase-orders/${pid}`);
      setDetail(data);
      setDetailOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openTracking = (po) => {
    setTrackingPo(po);
    setTrackingOpen(true);
  };

  const onTrackingSaved = async () => {
    await load();
    if (trackingPo?.id) {
      try {
        const { data } = await api.get(`/purchase-orders/${trackingPo.id}`);
        if (detailOpen) setDetail(data);
      } catch { /* ignore */ }
    }
  };

  const approveReceived = async () => {
    if (!detail) return;
    if (!rollsComplete) {
      toast.error("Enter a batch number and manufacturing date for every roll first.");
      return;
    }
    setReceiving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${detail.id}/receive`, {
        rolls: rollForms.map((f) => ({
          batch_number: f.batch_number.trim(),
          manufacturing_date: f.manufacturing_date,
          location: (f.location || "").trim(),
        })),
      });
      const bits = [];
      if (data.rolls_created) bits.push(`${data.rolls_created} rolls (${data.meters}m) → active stock`);
      if (data.units_added) bits.push(`${data.units_added} units → stock`);
      if (data.consumed_rolls) bits.push(`${data.consumed_rolls} rolls consumed for orders`);
      if (data.consumed_units) bits.push(`${data.consumed_units} units consumed for orders`);
      toast.success(`Shipment received — ${bits.join(", ") || "no stock movement"}`);
      setDetailOpen(false);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setReceiving(false); }
  };

  const deletePo = async (pid) => {
    try {
      await api.delete(`/purchase-orders/${pid}`);
      toast.success("Shipment cancelled");
      setDetailOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const allRollsSelected = rolls.length > 0 && selected.size === rolls.length;

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Procurement · Inbound</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Truck size={30} /> Incoming Stock
          </h1>
          <p className="text-sm text-slate-600 mt-2">Shipments and individual rolls in one place. Nothing enters active stock until it's received.</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button onClick={onReceiveSelected} className="bg-emerald-600 hover:bg-emerald-700 gap-2 rounded-md" data-testid="incoming-receive-selected-btn">
              <CheckCircle size={16} weight="fill" /> Receive {selected.size}
            </Button>
          )}
          {selected.size > 0 && (
            <Button onClick={() => setPrintOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-2 rounded-md" data-testid="incoming-print-btn">
              <Printer size={16} /> Print {selected.size} label{selected.size === 1 ? "" : "s"}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-incoming-btn">
                <Plus size={16} /> Add Incoming <CaretDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setImportOpen(true)} className="gap-2 cursor-pointer" data-testid="menu-import-shipment">
                <UploadSimple size={16} /> Import Shipment (Excel/CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openCreate} disabled={rollProducts.length === 0} className="gap-2 cursor-pointer" data-testid="menu-register-roll">
                <Package size={16} /> Register Individual Roll
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {rows.length === 0 && receivedBlocks.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center rounded-md" data-testid="no-incoming">
          <Truck size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500 max-w-md mx-auto">
            Nothing inbound yet. Use <strong>Add Incoming</strong> to import a shipment (Excel/CSV) or register an individual roll.
          </div>
        </div>
      ) : rows.length > 0 ? (
        <div className="border border-slate-200 bg-white rounded-md overflow-x-auto" data-testid="incoming-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-3 py-3 w-10">
                  <Checkbox
                    checked={allRollsSelected}
                    onCheckedChange={(v) => setSelected(v ? new Set(rolls.map((r) => r.id)) : new Set())}
                    data-testid="incoming-select-all"
                  />
                </th>
                <th className="px-3 py-3">Type / Supply</th>
                <th className="px-4 py-3">Reference / Item</th>
                <th className="px-4 py-3">Details</th>
                <th className="px-4 py-3">Date Ordered</th>
                <th className="px-4 py-3">ETA</th>
                <th className="px-4 py-3 text-right">Quantity</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                if (row._type === "po") {
                  return (
                    <tr key={`po-${row.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(row.id)} data-testid={`po-row-${row.id}`}>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <TypeTag type="po" />
                          <SupplyBadge type={row.shipping_type} />
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium">{row.reference}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        <div>{row.supplier || "—"}</div>
                        <div className="text-[10px] text-slate-400 mb-1">{row.item_count} line item{row.item_count === 1 ? "" : "s"}</div>
                        {row.status === "pending" && <LocationChip location="In Transit" />}
                        {row.tracking_number ? <div className="mt-1"><TrackingLink po={row} /></div> : null}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600" data-testid={`po-ordered-${row.id}`}>{fmtOrdered(row.created_at)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.eta || "—"}</td>
                      <td className="px-4 py-3 font-mono text-right text-xs tabular-nums">{row.total_rolls} rolls<div className="text-[10px] text-slate-400">{row.total_meters.toFixed(2)}m</div></td>
                      <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <Button size="sm" variant="ghost" className="gap-1 text-slate-700" onClick={(e) => { e.stopPropagation(); openDetail(row.id); }} data-testid={`po-view-${row.id}`}>
                            <Eye size={14} /> View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                // roll row
                const p = productMap[row.product_id];
                const mfr = p ? mfrMap[p.manufacturer_id] : null;
                const ser = p ? seriesMap[p.series_id] : null;
                const isSelected = selected.has(row.id);
                return (
                  <tr key={`roll-${row.id}`} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${isSelected ? "bg-blue-50/40" : ""}`} data-testid={`roll-row-${row.id}`}>
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(v) => {
                          const next = new Set(selected);
                          if (v) next.add(row.id); else next.delete(row.id);
                          setSelected(next);
                        }}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <TypeTag type="roll" />
                        <SupplyBadge type={row.supply_type} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {p ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-4 h-4 border border-slate-200 rounded-sm shrink-0" style={{ background: p.color_hex || "#e2e8f0" }} />
                          <div className="min-w-0">
                            <div className="font-medium truncate">{p.name}</div>
                            <div className="font-mono text-[10px] text-slate-500 truncate">{row.sku}</div>
                          </div>
                        </div>
                      ) : <span className="font-mono text-xs">{row.sku}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <div>{mfr?.name}{ser ? ` · ${ser.name}` : ""}</div>
                      <div className="font-mono text-[10px] text-slate-400 mb-1">Batch {row.batch_number}</div>
                      <LocationChip location={row.location} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600" data-testid={`roll-ordered-${row.id}`}>{fmtOrdered(row.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.expected_arrival_date || "—"}</td>
                    <td className="px-4 py-3 font-mono text-right tabular-nums">{row.initial_length_m.toFixed(2)}m</td>
                    <td className="px-4 py-3"><StatusBadge status="incoming" /></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button asChild size="sm" variant="ghost"><Link to={`/rolls/${row.id}`}><ArrowSquareOut size={14} /></Link></Button>
                        <Button onClick={() => onReceive(row.id)} size="sm" variant="ghost" className="text-emerald-700 hover:text-emerald-800 gap-1" data-testid={`receive-${row.id}`}>
                          <CheckCircle size={14} weight="fill" /> Receive
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-red-600"><Trash size={14} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel inbound roll?</AlertDialogTitle>
                              <AlertDialogDescription>This will delete the placeholder roll {row.sku}. The printed label will become invalid.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-md">Keep</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDelete(row.id)} className="bg-red-600 hover:bg-red-700 rounded-md">Cancel inbound</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Received history, grouped by day */}
      {receivedBlocks.length > 0 && (
        <section className="mt-10" data-testid="received-history">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <CheckCircle size={18} weight="fill" className="text-emerald-600" /> Received history
            <span className="text-slate-400 font-mono text-sm">· last {30} days</span>
          </h2>
          <div className="space-y-5">
            {receivedBlocks.map(([date, list]) => (
              <div key={date} className="border border-slate-200 bg-white rounded-md overflow-hidden" data-testid={`received-block-${date}`}>
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <CalendarBlank size={16} className="text-slate-500" />
                  <span className="font-heading font-semibold text-sm text-slate-800">{fmtDay(date)}</span>
                  <span className="text-xs text-slate-400 font-mono">· {list.length} shipment{list.length === 1 ? "" : "s"}</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {list.map((po) => (
                    <li key={po.id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(po.id)} data-testid={`received-po-${po.id}`}>
                      <TypeTag type="po" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{po.reference}</div>
                        <div className="text-xs text-slate-500">{po.supplier || "—"} · {po.item_count} line item{po.item_count === 1 ? "" : "s"}</div>
                        {po.tracking_number ? <div className="mt-1"><TrackingLink po={po} /></div> : null}
                      </div>
                      <div className="font-mono text-right text-xs tabular-nums text-slate-600">
                        {po.total_rolls} rolls
                        <div className="text-[10px] text-slate-400">{po.total_meters.toFixed(2)}m</div>
                      </div>
                      <Button size="sm" variant="ghost" className="gap-1 text-slate-700" onClick={(e) => { e.stopPropagation(); openDetail(po.id); }} data-testid={`received-view-${po.id}`}>
                        <Eye size={14} /> View
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Register individual roll dialog (controlled) */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Register inbound roll</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Product</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="incoming-product-select"><SelectValue placeholder="Select product…" /></SelectTrigger>
                <SelectContent>
                  {rollProducts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {mfrMap[p.manufacturer_id]?.name} · {seriesMap[p.series_id]?.name} · {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Supply / freight type</Label>
              <Select value={form.supply_type} onValueChange={(v) => setForm({ ...form, supply_type: v })}>
                <SelectTrigger data-testid="incoming-supply-type"><SelectValue placeholder="Select supply type…" /></SelectTrigger>
                <SelectContent>
                  {SUPPLY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Batch number</Label>
              <Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="B-240115-A" className="font-mono" data-testid="incoming-batch" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Manufacturing date</Label>
              <Input type="date" value={form.manufacturing_date} onChange={(e) => setForm({ ...form, manufacturing_date: e.target.value })} className="font-mono" data-testid="incoming-mfg-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Expected length (m)</Label>
              <Input type="number" step="0.01" value={form.expected_length_m} onChange={(e) => setForm({ ...form, expected_length_m: e.target.value })} className="font-mono" data-testid="incoming-length" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Width (m)</Label>
              <Input type="number" step="0.01" value={form.width_m} onChange={(e) => setForm({ ...form, width_m: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={`font-medium ${form.location === "In Transit" ? "border-amber-300 bg-amber-50 text-amber-800" : ""}`} data-testid="incoming-location" />
              <p className="text-[10px] text-slate-400">Incoming stock stays “In Transit” until received.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Expected arrival</Label>
              <Input type="date" value={form.expected_arrival_date} onChange={(e) => setForm({ ...form, expected_arrival_date: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterOpen(false)} className="rounded-md">Cancel</Button>
            <Button onClick={onSave} disabled={submitting || !form.product_id || !form.batch_number} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="incoming-save-btn">
              {submitting ? "Saving…" : "Register"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import shipment dialog (controlled, no trigger) */}
      <ShipmentImportDialog open={importOpen} onOpenChange={setImportOpen} hideTrigger onSuccess={load} />

      {/* Shipment detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Boat size={20} /> {detail.reference}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 border border-slate-200 rounded-md overflow-hidden mb-4">
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Date Ordered</div>
                  <div className="text-sm font-mono mt-0.5" data-testid="po-detail-ordered">{fmtOrdered(detail.created_at)}</div>
                </div>
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Supplier</div>
                  <div className="text-sm font-medium mt-0.5">{detail.supplier || "—"}</div>
                </div>
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Shipping</div>
                  <div className="text-sm font-medium mt-0.5">{detail.shipping_type || "—"}</div>
                </div>
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">ETA</div>
                  <div className="text-sm font-mono mt-0.5">{detail.eta || "—"}</div>
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Status</div>
                  <div className="mt-1"><StatusBadge status={detail.status} /></div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 rounded-md p-3 mb-4 bg-slate-50/60" data-testid="po-tracking-section">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Courier tracking</div>
                  {detail.tracking_number ? (
                    <div className="mt-1.5"><TrackingLink po={detail} /></div>
                  ) : (
                    <div className="text-sm text-slate-400 mt-1">No tracking added yet.</div>
                  )}
                </div>
                <Button size="sm" variant="outline" className="gap-1.5 rounded-md shrink-0" onClick={() => openTracking(detail)} data-testid="po-edit-tracking-btn">
                  <Truck size={14} /> {detail.tracking_number ? "Edit tracking" : "Add tracking"}
                </Button>
              </div>

              <div className="border border-slate-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                      <th className="px-3 py-2">Brand</th>
                      <th className="px-3 py-2">Series</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">Unit</th>
                      <th className="px-3 py-2 text-right">Len/roll</th>
                      <th className="px-3 py-2 text-right">Total m</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, i) => {
                      const totalM = it.unit === "meters" ? it.quantity : it.quantity * (it.length_per_roll_m || 0);
                      return (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2">{it.manufacturer_name}</td>
                          <td className="px-3 py-2">{it.series_name}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {it.color_hex && <div className="w-3.5 h-3.5 border border-slate-200 rounded-sm shrink-0" style={{ background: it.color_hex }} />}
                              <span className="font-medium">{it.product_name}</span>
                              {it.purchase_type === "order" && (
                                <span className="text-[9px] font-mono tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-sm border bg-violet-50 text-violet-700 border-violet-200" data-testid={`po-item-order-${i}`}>
                                  For order · {it.order_reference || "—"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 font-mono text-right tabular-nums">{it.quantity}</td>
                          <td className="px-3 py-2">{it.unit}</td>
                          <td className="px-3 py-2 font-mono text-right">{it.length_per_roll_m ? `${it.length_per_roll_m}m` : "—"}</td>
                          <td className="px-3 py-2 font-mono text-right tabular-nums">{totalM.toFixed(2)}m</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50 font-medium">
                      <td className="px-3 py-2 text-xs text-slate-500" colSpan={3}>Total</td>
                      <td className="px-3 py-2 font-mono text-right tabular-nums">{detail.total_rolls}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">rolls</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 font-mono text-right tabular-nums">{detail.total_meters.toFixed(2)}m</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {detail.status === "pending" && (rollSlots.length > 0 || countItems.length > 0) && (
                <div className="mt-4 border border-slate-200 rounded-md" data-testid="receive-form">
                  <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-200">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <CheckCircle size={16} className="text-emerald-600" /> Receive stock — enter details for each roll
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Batch number and manufacturing date are required for every roll before the shipment can be received.</div>
                  </div>

                  {rollSlots.length > 0 && (
                    <div className="divide-y divide-slate-100">
                      {rollSlots.map((slot, i) => {
                        const f = rollForms[i] || { batch_number: "", manufacturing_date: "", location: "" };
                        const done = f.batch_number.trim() && f.manufacturing_date.trim();
                        return (
                          <div key={slot.key} className="p-3" data-testid={`roll-slot-${i}`}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                {slot.color_hex && <div className="w-3.5 h-3.5 border border-slate-200 rounded-sm shrink-0" style={{ background: slot.color_hex }} />}
                                <span className="text-sm font-medium text-slate-900 truncate">Roll {i + 1} · {slot.product_name}</span>
                                <span className="text-[11px] font-mono text-slate-500 shrink-0">{slot.length}m</span>
                                {slot.is_order && (
                                  <span className="text-[9px] font-mono tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-sm border bg-violet-50 text-violet-700 border-violet-200 shrink-0">For order · {slot.order_ref || "—"}</span>
                                )}
                                {done && <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />}
                              </div>
                              {rollSlots.length > 1 && i < rollSlots.length - 1 && done && (
                                <button type="button" onClick={() => fillDown(i)} className="text-[11px] text-blue-600 hover:underline shrink-0 flex items-center gap-0.5" data-testid={`fill-down-${i}`}>
                                  <CaretDown size={11} /> Fill down
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <div className="space-y-1">
                                <Label className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Batch number *</Label>
                                <Input value={f.batch_number} onChange={(e) => setRoll(i, { batch_number: e.target.value })} placeholder="e.g. B-24051" className="font-mono h-9" data-testid={`roll-batch-${i}`} />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Manufacturing date *</Label>
                                <Input type="date" value={f.manufacturing_date} onChange={(e) => setRoll(i, { manufacturing_date: e.target.value })} className="h-9" data-testid={`roll-mfg-${i}`} />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Location</Label>
                                <Input value={f.location} onChange={(e) => setRoll(i, { location: e.target.value })} placeholder={slot.default_location || "e.g. Shelf A1"} className="h-9" data-testid={`roll-location-${i}`} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {countItems.length > 0 && (
                    <div className="p-3 border-t border-slate-200 bg-slate-50/40 text-xs text-slate-600 space-y-1">
                      {countItems.map((it, i) => (
                        <div key={i} className="flex items-center gap-1.5" data-testid={`count-item-note-${i}`}>
                          <Package size={13} className="text-slate-400" />
                          <span className="font-medium">{it.quantity} × {it.product_name}</span>
                          <span className="text-slate-400">— {it.purchase_type === "order" ? `consumed for order ${it.order_reference || "—"} (no batch needed)` : "added to stock as units (no batch needed)"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="gap-2 sm:gap-2">
                {detail.status === "pending" ? (
                  <>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="text-red-600 rounded-md gap-1.5" data-testid="po-delete-btn"><Trash size={15} /> Cancel shipment</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel this shipment?</AlertDialogTitle>
                          <AlertDialogDescription>This deletes the purchase order "{detail.reference}" and its line items. No stock will be created.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-md">Keep</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deletePo(detail.id)} className="bg-red-600 hover:bg-red-700 rounded-md">Delete shipment</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button onClick={approveReceived} disabled={receiving || !rollsComplete} className="bg-emerald-600 hover:bg-emerald-700 rounded-md gap-2 disabled:opacity-50" data-testid="po-approve-received-btn" title={!rollsComplete ? "Enter batch number and manufacturing date for every roll first" : undefined}>
                      <CheckCircle size={16} weight="fill" /> {receiving ? "Receiving…" : rollsComplete ? "Approve stock received" : `Fill in ${rollSlots.length} roll${rollSlots.length === 1 ? "" : "s"} to receive`}
                    </Button>
                  </>
                ) : (
                  <div className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle size={16} weight="fill" /> Received {detail.received_at ? new Date(detail.received_at).toLocaleDateString() : ""} — stock is now active</div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PrintLabelsDialog open={printOpen} onOpenChange={setPrintOpen} rollIds={Array.from(selected)} />
      <ReceiveRollDialog
        open={receiveOpen}
        onOpenChange={setReceiveOpen}
        count={receiveTargets.length}
        submitting={receiveSubmitting}
        onConfirm={confirmReceive}
      />
      <TrackingDialog open={trackingOpen} onOpenChange={setTrackingOpen} po={trackingPo} onSuccess={onTrackingSaved} />
    </div>
  );
}
